import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  documents,
  policies,
  type PolicyDetail,
  type PolicyInstallmentInput,
  type PolicyInstallmentStatus,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { moneyInputError, moneyToInput, toMoney } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { MoneyField } from '@/shared/ui/MoneyField';
import { Select } from '@/shared/ui/Select';
import { statusLabel } from '@/shared/ui/statusLabels';
import { useToast } from '@/shared/ui/useToast';

interface LinhaDaParcela {
  chave: string;
  number: string;
  amount: string;
  interest: string;
  due_on: string;
  status: PolicyInstallmentStatus;
  paid_on: string;
  billet_document_id: string;
  billet_number: string;
}

const situacoes: PolicyInstallmentStatus[] = [
  'em_aberto',
  'paga',
  'vencida',
  'cancelada',
];

let proximaChave = 0;
function novaChave() {
  proximaChave += 1;
  return `parcela-${proximaChave}`;
}

/**
 * Os boletos ja anexados as parcelas que nao estao entre os documentos da apolice: o
 * boleto copiado da cotacao na emissao, que e documento da operacao.
 */
function boletosJaAnexados(
  policy: PolicyDetail,
  daApolice: Set<string>,
): { value: string; label: string }[] {
  const vistos = new Set<string>();
  const saida: { value: string; label: string }[] = [];
  for (const parcela of policy.installments) {
    const documento = parcela.billet_document_id;
    if (!documento || daApolice.has(documento) || vistos.has(documento)) continue;
    vistos.add(documento);
    const origem =
      parcela.source === 'cotacao' ? 'Boleto copiado da cotacao' : 'Boleto ja anexado';
    saida.push({
      value: documento,
      label: parcela.billet_number ? `${origem} (${parcela.billet_number})` : origem,
    });
  }
  return saida;
}

/**
 * "Informar parcelas" da apolice (P-APO-1).
 *
 * As parcelas nao saem de calculo: o parcelamento, os juros e os vencimentos sao regra
 * pendente (PAR-001 a PAR-007), e a copia das parcelas da cotacao na emissao chega com
 * o P-COT-4. Ate la, quem tem a alcada (`apolices.parcela.update`) informa, e a trilha
 * guarda antes e depois. A soma aparece ao lado do premio so para conferencia: nenhuma
 * regra a compara com o premio. O boleto so e anexado quando a empresa usa o boleto
 * anexado.
 */
export function PolicyInstallmentsDialog({
  policy,
  open,
  onClose,
}: {
  policy: PolicyDetail;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [linhas, setLinhas] = useState<LinhaDaParcela[]>(() =>
    policy.installments.map((parcela) => ({
      chave: novaChave(),
      number: String(parcela.number),
      amount: moneyToInput(parcela.amount),
      interest: parcela.interest.amount === 0 ? '' : moneyToInput(parcela.interest),
      due_on: parcela.due_on.slice(0, 10),
      status: parcela.status,
      paid_on: parcela.paid_on?.slice(0, 10) ?? '',
      billet_document_id: parcela.billet_document_id ?? '',
      billet_number: parcela.billet_number,
    })),
  );

  const comBoleto = policy.billet_mode === 'anexo';
  const boletos = useQuery({
    queryKey: ['documents', { owner_type: 'apolice', owner_id: policy.id }],
    queryFn: ({ signal }) =>
      documents.list(
        { owner_type: 'apolice', owner_id: policy.id, limit: 100 },
        signal,
      ),
    enabled: open && comBoleto && can('documentos.documento.view'),
  });
  const documentosComArquivo = (boletos.data?.items ?? []).filter(
    (d) => d.current_version > 0,
  );
  // O boleto que ja esta anexado continua escolhivel, mesmo sem ser documento da
  // apolice: a emissao copia o boleto da cotacao, que e documento da operacao, e o
  // servidor aceita o boleto que nao mudou (R5-F01). Sem ele na lista, a selecao
  // mostrava "Sem boleto" e enviava o boleto de qualquer jeito.
  const opcoesDeBoleto = [
    ...documentosComArquivo.map((d) => ({ value: d.id, label: d.title })),
    ...boletosJaAnexados(policy, new Set(documentosComArquivo.map((d) => d.id))),
  ];

  const gravar = useMutation({
    mutationFn: (itens: PolicyInstallmentInput[]) =>
      policies.replaceInstallments(policy.id, itens),
    onSuccess: () => {
      toast.success('Parcelas da apolice gravadas.');
      void queryClient.invalidateQueries({ queryKey: ['policy', policy.id] });
      void queryClient.invalidateQueries({
        queryKey: ['policy-installments', policy.id],
      });
      void queryClient.invalidateQueries({ queryKey: ['policy-billets', policy.id] });
      onClose();
    },
  });
  const erro = gravar.error instanceof ApiError ? gravar.error : null;

  function muda(chave: string, campo: keyof LinhaDaParcela, valor: string) {
    setLinhas((atuais) =>
      atuais.map((linha) =>
        linha.chave === chave ? { ...linha, [campo]: valor } : linha,
      ),
    );
  }

  const invalida = linhas.some(
    (linha) =>
      moneyInputError(linha.amount) !== undefined ||
      (linha.interest !== '' && moneyInputError(linha.interest) !== undefined) ||
      linha.due_on === '' ||
      linha.number === '',
  );

  function enviar() {
    gravar.mutate(
      linhas.map((linha) => ({
        number: Number(linha.number),
        amount: toMoney(linha.amount, policy.premium.currency),
        ...(linha.interest !== ''
          ? { interest: toMoney(linha.interest, policy.premium.currency) }
          : {}),
        due_on: linha.due_on,
        status: linha.status,
        paid_on: linha.status === 'paga' && linha.paid_on !== '' ? linha.paid_on : null,
        billet_document_id:
          comBoleto && linha.billet_document_id !== ''
            ? linha.billet_document_id
            : null,
        billet_number: linha.billet_number,
      })),
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Informar parcelas"
      description="Parcelas, vencimentos e situacao da apolice, informados por quem tem a alcada."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button disabled={invalida} loading={gravar.isPending} onClick={enviar}>
            Gravar parcelas
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Alert tone="info" title="Valores informados">
          O SeguraCore nao calcula parcelamento, juros nem vencimentos: a regra esta
          pendente. A soma das parcelas aparece ao lado do premio so para conferencia.
        </Alert>
        {gravar.isError ? (
          <Alert tone="danger" title="Nao foi possivel gravar">
            {gravar.error.message}
            {typeof erro?.details?.field === 'string' ? ` (${erro.details.field})` : ''}
          </Alert>
        ) : null}
        {linhas.length === 0 ? (
          <p className="text-sm text-ink-600">Nenhuma parcela informada.</p>
        ) : null}
        {linhas.map((linha, indice) => (
          <fieldset
            key={linha.chave}
            className="grid gap-3 rounded-md border border-line p-3 sm:grid-cols-4"
          >
            <legend className="px-1 text-xs font-semibold text-ink-700">
              Parcela {indice + 1}
            </legend>
            <Field
              label="Numero da parcela"
              inputMode="numeric"
              value={linha.number}
              onChange={(event) => muda(linha.chave, 'number', event.target.value)}
            />
            <MoneyField
              label="Valor da parcela (R$)"
              currency={policy.premium.currency}
              value={linha.amount}
              error={linha.amount === '' ? undefined : moneyInputError(linha.amount)}
              onValueChange={(texto) => muda(linha.chave, 'amount', texto)}
            />
            <MoneyField
              label="Juros (R$)"
              currency={policy.premium.currency}
              value={linha.interest}
              error={
                linha.interest === '' ? undefined : moneyInputError(linha.interest)
              }
              onValueChange={(texto) => muda(linha.chave, 'interest', texto)}
            />
            <Field
              label="Vencimento"
              type="date"
              value={linha.due_on}
              onChange={(event) => muda(linha.chave, 'due_on', event.target.value)}
            />
            <Select
              label="Situacao da parcela"
              value={linha.status}
              options={situacoes.map((situacao) => ({
                value: situacao,
                label: statusLabel('parcela', situacao),
              }))}
              onChange={(event) => muda(linha.chave, 'status', event.target.value)}
            />
            {linha.status === 'paga' ? (
              <Field
                label="Pago em"
                type="date"
                value={linha.paid_on}
                onChange={(event) => muda(linha.chave, 'paid_on', event.target.value)}
              />
            ) : null}
            {comBoleto ? (
              <>
                <Select
                  label="Boleto anexado"
                  placeholder="Sem boleto"
                  value={linha.billet_document_id}
                  options={opcoesDeBoleto}
                  onChange={(event) =>
                    muda(linha.chave, 'billet_document_id', event.target.value)
                  }
                />
                <Field
                  label="Numero do boleto"
                  value={linha.billet_number}
                  maxLength={60}
                  onChange={(event) =>
                    muda(linha.chave, 'billet_number', event.target.value)
                  }
                />
              </>
            ) : null}
            <div className="flex items-end">
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  setLinhas((atuais) =>
                    atuais.filter((atual) => atual.chave !== linha.chave),
                  )
                }
              >
                Retirar parcela
              </Button>
            </div>
          </fieldset>
        ))}
        <div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              setLinhas((atuais) => [
                ...atuais,
                {
                  chave: novaChave(),
                  number: String(atuais.length + 1),
                  amount: '',
                  interest: '',
                  due_on: '',
                  status: 'em_aberto',
                  paid_on: '',
                  billet_document_id: '',
                  billet_number: '',
                },
              ])
            }
          >
            Acrescentar parcela
          </Button>
        </div>
      </div>
    </Modal>
  );
}
